const MAX_FILE_BYTES = 25 * 1024 * 1024;
const THUMBNAIL_MAX_DIMENSION = 480;
const THUMBNAIL_QUALITY = 0.72;
const THUMBNAIL_TIMEOUT_MS = 12_000;

export function validateImageFile(file: File): void {
  if (file.type && !file.type.startsWith("image/")) {
    throw new Error("画像ファイルを選択してください。");
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("写真は25MB以下にしてください。");
  }
}

export async function prepareImageForUpload(file: File): Promise<File> {
  validateImageFile(file);
  return file;
}

function thumbnailName(originalName: string): string {
  const base = originalName
    .replace(/\.[^.]+$/u, "")
    .replace(/[^a-zA-Z0-9_-]+/gu, "-")
    .slice(0, 48) || "photo";
  const random = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `${base}-thumb-${Date.now()}-${random}.jpg`;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    let finished = false;
    let timer = 0;

    const cleanup = () => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      image.onload = null;
      image.onerror = null;
    };
    const finishError = () => {
      if (finished) return;
      finished = true;
      cleanup();
      reject(new Error("サムネイルを生成できませんでした。"));
    };
    image.onload = () => {
      if (finished) return;
      finished = true;
      cleanup();
      resolve(image);
    };
    image.onerror = finishError;
    timer = window.setTimeout(finishError, THUMBNAIL_TIMEOUT_MS);
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    let finished = false;
    const timer = window.setTimeout(() => {
      if (finished) return;
      finished = true;
      reject(new Error("サムネイル生成がタイムアウトしました。"));
    }, THUMBNAIL_TIMEOUT_MS);
    canvas.toBlob((blob) => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      if (blob && blob.size > 0) resolve(blob);
      else reject(new Error("サムネイルを生成できませんでした。"));
    }, "image/jpeg", THUMBNAIL_QUALITY);
  });
}

export async function createImageThumbnail(file: File): Promise<File | null> {
  try {
    validateImageFile(file);
    const image = await loadImage(file);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!width || !height) return null;

    const scale = Math.min(1, THUMBNAIL_MAX_DIMENSION / Math.max(width, height));
    const outputWidth = Math.max(1, Math.round(width * scale));
    const outputHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext("2d");
    if (!context) return null;

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, outputWidth, outputHeight);
    context.drawImage(image, 0, 0, outputWidth, outputHeight);
    const blob = await canvasToBlob(canvas);
    return new File([blob], thumbnailName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    // サムネイル生成に失敗しても、元画像の保存は成功扱いにする。
    return null;
  }
}
